import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import { NgClass, NgOptimizedImage } from "@angular/common";
import {ParisDatePipe} from '../../../../pipes/date/paris-date.pipe';
import {PaginatorComponent} from "../../../layout/paginator/paginator.component";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {TableSorterHeaderDirective} from "../../../../directives/table-sorter/table-sorter-header.directive";
import {Paginator} from '../../../../tools/pagination/paginator';
import {TableSorter} from '../../../../tools/table-sorter/table-sorter';
import {LoaderService} from '../../../../services/loader/loader.service';
import {Conversion} from '../../../../models/conversion';
import {ConversionService} from '../../../../services/conversion/conversion.service';
import {TableSorterDirective} from '../../../../directives/table-sorter/table-sorter.directive';
import {CurrencyPipe} from '../../../../pipes/currency/currency.pipe';
import {DialogService} from '../../../../services/dialog/dialog.service';
import {ConversionDetailComponent} from './conversion-detail/conversion-detail.component';
import {IsGranted} from '../../../../decorator/is-granted';
import {MetaTitle} from '../../../../decorator/meta-title';
import {ConfirmDirective} from '../../../../directives/confirm/confirm.directive';
import {CountryService} from '../../../../services/country/country.service';
import {TooltipDirective} from '../../../../directives/tooltip/tooltip.directive';
import {QueryFilterService} from '../../../../services/query-filter/query-filter.service';
import {ClickDetailComponent} from '../click-log/click-detail/click-detail.component';
import {LeadDetailComponent} from '../lead-log/lead-detail/lead-detail.component';
import {CheckboxTable} from '../../../../tools/checkbox-table/checkbox-table';
import {CheckboxTableComponent} from '../../../layout/checkbox-table/checkbox-table.component';
import {CheckboxTableDirective} from '../../../../directives/checkbox-table/checkbox-table.directive';
import {CheckboxAllTableComponent} from '../../../layout/checkbox-all-table/checkbox-all-table.component';
import {ConversionAdjustPayoutComponent} from './conversion-adjust-payout/conversion-adjust-payout.component';
import {ConversionBatchComponent} from './conversion-batch/conversion-batch.component';
import {DisplayTableColumnComponent} from '../../../layout/display-table-column/display-table-column.component';
import {LogFiltersComponent} from '../../../layout/log-filters/log-filters.component';
import {LogFilter} from '../../../../types/log-filter';
import {LogForm} from '../../../../forms/log-form';
import {DropdownSelectComponent} from '../../../layout/dropdown-select/dropdown-select.component';
import {DropdownSelectOptionComponent} from '../../../layout/dropdown-select-option/dropdown-select-option.component';
import {GoalService} from '../../../../services/goal/goal.service';
import {Goal} from '../../../../models/goal';
import {GoalType} from '../../../../models/goal-type';
import {getTableHeaders} from '../../../../helpers/table.helper';
import {IsGrantedDirective} from '../../../../directives/is-granted/is-granted.directive';
import {CsvExportService} from '../../../../services/csv-export/csv-export.service';
import {SnackbarService} from '../../../../services/snackbar/snackbar.service';
import {CsvExportProgressComponent} from '../../../layout/csv-export-progress/csv-export-progress.component';

@Component({
  selector: 'app-conversion-log',
  imports: [
    ParisDatePipe,
    PaginatorComponent,
    ReactiveFormsModule,
    TableSorterHeaderDirective,
    TableSorterDirective,
    FormsModule,
    CurrencyPipe,
    ConfirmDirective,
    NgOptimizedImage,
    TooltipDirective,
    NgClass,
    CheckboxTableComponent,
    CheckboxTableDirective,
    CheckboxAllTableComponent,
    DisplayTableColumnComponent,
    LogFiltersComponent,
    DropdownSelectComponent,
    DropdownSelectOptionComponent,
    IsGrantedDirective,
    CsvExportProgressComponent,
  ],
  templateUrl: './conversion-log.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './conversion-log.component.scss'
})
@IsGranted(['CONVERSION_VIEW'])
@MetaTitle('Conversion logs')
export class ConversionLogComponent implements OnInit {

  conversions: Conversion[] = [];

  paginator: Paginator = new Paginator();
  tableSorter: TableSorter = new TableSorter('createdAt', 'desc');
  checkboxTable: CheckboxTable = new CheckboxTable();

  goalList: Goal[] = [];
  goalTypeList: GoalType[] = [];

  displayedColumns = {
    goal: true,
    goalType: true,
    amount: true,
    customPayout: true,
    cid: true,
    lead: true,
    product: true,
    offer: true,
    conversionType: false,
    accountType: false,
    accountManager: false,
    landingPage: false,
    partner: true,
    payout: true,
    subid: false,
    tc1: false,
    tc2: false,
    tc3: false,
    tc4: false,
    clickid: false,
    gclid: false,
    fbclid: false,
    fbpxid: false,
    msclkid: false,
    mspxid: false,
    twclid: false,
    twpxid: false,
    ttclid: false,
    ttpxid: false,
    source: false,
    ip: false,
    referer: false,
    customerName: false,
    customerLanguage: false,
    customerCountry: true,
    customerEmail: false,
    uniqueReference: false,
    conversionDate: false,
  };

  filters: LogFilter = LogForm.initFilters({
    cid: '',
    goalId: [],
    goalTypeId: [],
    accepted: null,
    paid: null,
    email: null,
  });

  batchAction = '';

  downloading: boolean = false;
  exportProgress: { current: number; total: number } | null = null;

  constructor(
    private loader: LoaderService,
    private dialog: DialogService,
    private conversionService: ConversionService,
    private queryFilterService: QueryFilterService,
    private goalService: GoalService,
    protected countryService: CountryService,
    private csvExportService: CsvExportService,
    private snackbar: SnackbarService,
  ) {
  }

  ngOnInit(): void {
    this.queryFilterService.init(this.paginator, this.tableSorter);

    this.filters = this.queryFilterService.initFilters();
    this.filters['cid'] = this.queryFilterService.getFilter('cid');
    this.filters['goalId'] = this.queryFilterService.getNumberListFilter('goalId');
    this.filters['goalTypeId'] = this.queryFilterService.getNumberListFilter('goalTypeId');
    this.filters['accepted'] = this.queryFilterService.getBooleanFilter('accepted');
    this.filters['paid'] = this.queryFilterService.getBooleanFilter('paid');
    this.filters['email'] = this.queryFilterService.getFilter('email');

    this.loader.startLoading();

    this.goalService.findAll({groups: 'types', sort: 'name'}).subscribe((goals: Goal[]) => {
      this.goalList = goals;
      // Populate the goal type list for the goals restored from the URL
      this.updateGoals();
    });
  }

  applyFilters(): void {
    this.paginator.currentPage = 1;
    this.fetchAll();
  }

  fetchAll(): void {
    this.loader.startLoading();

    const params: any = this.getParameters();

    this.queryFilterService.setFilters(params);

    this.conversionService.findAll(params, this.paginator, this.tableSorter).subscribe(conversions => {
      this.conversions = conversions;
      this.loader.stopLoading();
    });
  }

  getAmount(conversion: Conversion): string {
    const amount = conversion.currencyAmount ? conversion.currencyAmount / 100 : 0;

    return amount.toString();
  }

  getPayout(conversion: Conversion): string {
    const payout = conversion.payout ? conversion.payout / 100 : '';

    return payout.toString();
  }

  getCurrencyPayout(conversion: Conversion): string {
    const payout = conversion.currencyPayout ? conversion.currencyPayout / 100 : '';

    return payout.toString();
  }

  getCurrency(conversion: Conversion): string {
    return conversion?.currency?.isoCode ?? '';
  }

  openDetail(conversion: Conversion): void {
    this.dialog.openDialog(ConversionDetailComponent, {
      class: 'modal-lg',
      data: {
        id: conversion.id
      }
    }).subscribe(() => {
    });
  }

  accept(conversion: Conversion, accepted: boolean): void {
    if (conversion.id) {
      this.loader.startLoading();
      this.conversionService.update(conversion.id, {accepted}).subscribe(() => {
        this.fetchAll();
      });
    }
  }

  getClass(conversion: Conversion): string[] {
    let classes = [];

    if (!conversion.accepted) {
      classes.push('status-rejected');
    }

    return classes;
  }

  openClickDetail(conversion: Conversion): void {
    if (conversion.cid) {
      this.dialog.openDialog(ClickDetailComponent, {
        class: 'modal-lg',
        data: {
          id: conversion.cid
        }
      }).subscribe(() => {
      });
    }
  }

  openLeadDetail(conversion: Conversion): void {
    if (conversion.leadId) {
      this.dialog.openDialog(LeadDetailComponent, {
        class: 'modal-lg',
        data: {
          id: conversion.leadId
        }
      }).subscribe(() => {
      });
    }
  }

  submitBatch(): void {
    if (this.checkboxTable.selectedValues.length === 0) {
      return;
    }

    let batch = {
      action: this.batchAction,
      conversions: this.checkboxTable.selectedValues,
      params: {}
    }

    switch (this.batchAction) {
      case 'accept-conversion':
      case 'reject-conversion':
        this.runBatch(batch);
        break
      case 'adjust-payout':
        this.dialog.openDialog(ConversionAdjustPayoutComponent, {}).subscribe((result) => {
          if (result.payout !== null) {
            batch.params = {
              payout: result.payout
            };
            this.runBatch(batch);
          }
        });
        break
    }
  }

  runBatch(batch: any): void {
    this.dialog.openDialog(ConversionBatchComponent, {
      data: {
        batch
      }
    }).subscribe(() => {
      this.fetchAll();
    });
  }

  updateGoals(): void {
    const goalTypes: GoalType[] = [];

    for (const goalId of this.filters['goalId']) {
      const goal = this.goalList.find(goal => goal.id === goalId);
      if (goal) {
        for (const type of goal.types) {
          goalTypes.push(type.deserialize({goalIdentifier: goal.identifier}));
        }
      }
    }

    this.goalTypeList = goalTypes.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
  }

  downloadCsv(): void {
    if (this.downloading) {
      return;
    }

    const params = this.getParameters();
    const headers = getTableHeaders('conversions');
    const amountIndex = headers.indexOf('Amount');
    if (amountIndex !== -1) {
      headers.splice(amountIndex + 1, 0, 'Amount EUR');
    }
    params['headers'] = headers;

    if (!params.startDate || !params.endDate) {
      this.snackbar.error('Please select a date range before exporting.');
      return;
    }

    this.downloading = true;
    this.exportProgress = null;

    this.csvExportService.export({
      endpoint: '/conversion/download',
      params,
      filename: 'conversion-logs.csv'
    }).subscribe({
      next: (event) => {
        if (event.type === 'progress') {
          this.exportProgress = { current: event.current, total: event.total };
        } else {
          this.downloading = false;
          this.exportProgress = null;
          if (event.truncatedChunks > 0) {
            this.snackbar.warning(
              `${event.truncatedChunks} of ${event.chunkCount} chunk(s) were truncated. The export may be incomplete — try a smaller date range.`
            );
          }
        }
      },
      error: () => {
        this.downloading = false;
        this.exportProgress = null;
        this.snackbar.error('CSV export failed. Please try again.');
      }
    });
  }

  private getParameters(): any {
    let params: any = LogForm.getFilters(this.filters, 'landingPage,product,offer,partner,accountManager,minAge,customPayout');

    if (this.filters['cid']) {
      params.cid = this.filters['cid'];
    }

    if (this.filters['goalId'] && this.filters['goalId'].length) {
      params.goalId = this.filters['goalId'].join(',');
    }

    if (this.filters['goalTypeId'] && this.filters['goalTypeId'].length) {
      params.goalTypeId = this.filters['goalTypeId'].join(',');
    }

    if (this.filters['accepted'] !== null) {
      params.accepted = this.filters['accepted'] ? 1 : 0;
    }

    if (this.filters['paid'] !== null) {
      params.paid = this.filters['paid'] ? 1 : 0;
    }

    if (this.filters['email']) {
      params.email = this.filters['email'];
    }

    return params;
  }
}
